// Robust document handling utilities
export const documentHandler = {
  // Validate if a value is a valid URL
  isValidUrl: (string) => {
    try {
      new URL(string);
      return true;
    } catch (_) {
      return false;
    }
  },

  // Safely extract document properties with comprehensive validation
  extractDocumentData: (doc, key) => {
    // Handle null/undefined
    if (doc === null || doc === undefined) {
      return documentHandler.getDefaultDocumentData(key);
    }

    // Handle string URLs
    if (typeof doc === 'string') {
      return {
        url: documentHandler.isValidUrl(doc) ? doc : '',
        name: documentHandler.generateDocumentName(key),
        words: 0,
        cost: 0,
        language: 'Unknown',
        type: 'url',
        isValid: documentHandler.isValidUrl(doc)
      };
    }

    // Handle objects
    if (typeof doc === 'object') {
      // Handle arrays (convert to object)
      if (Array.isArray(doc)) {
        return documentHandler.getDefaultDocumentData(key);
      }

      // Handle regular objects
      const url = doc.url || doc.link || doc.file || '';
      const name = doc.name || doc.title || doc.filename || documentHandler.generateDocumentName(key);
      const words = documentHandler.parseNumber(doc.words, 0);
      const cost = documentHandler.parseNumber(doc.cost, 0);
      const language = doc.language || doc.lang || 'Unknown';
      const type = doc.type || doc.fileType || 'document';

      return {
        url: documentHandler.isValidUrl(url) ? url : '',
        name: documentHandler.sanitizeString(name),
        words: Math.max(0, words),
        cost: Math.max(0, cost),
        language: documentHandler.sanitizeString(language),
        type: documentHandler.sanitizeString(type),
        isValid: documentHandler.isValidUrl(url),
        // Preserve additional properties
        ...doc
      };
    }

    // Handle numbers (treat as cost)
    if (typeof doc === 'number') {
      return {
        url: '',
        name: documentHandler.generateDocumentName(key),
        words: 0,
        cost: Math.max(0, doc),
        language: 'Unknown',
        type: 'cost',
        isValid: false
      };
    }

    // Handle booleans
    if (typeof doc === 'boolean') {
      return {
        url: '',
        name: documentHandler.generateDocumentName(key),
        words: 0,
        cost: 0,
        language: 'Unknown',
        type: 'boolean',
        isValid: false
      };
    }

    // Fallback for any other type
    return documentHandler.getDefaultDocumentData(key);
  },

  // Generate a readable document name from key
  generateDocumentName: (key) => {
    if (!key || typeof key !== 'string') {
      return 'Unknown Document';
    }

    return key
      .replace(/document_/g, 'Document ')
      .replace(/\[|\]/g, '')
      .replace(/\[\d+\]/g, '')
      .split(/(?=[A-Z])/)
      .join(' ')
      .replace(/_/g, ' ')
      .replace(/^\w/, (c) => c.toUpperCase())
      .trim() || 'Document';
  },

  // Parse number safely
  parseNumber: (value, defaultValue = 0) => {
    if (typeof value === 'number' && !isNaN(value)) {
      return value;
    }
    if (typeof value === 'string') {
      const parsed = parseFloat(value);
      return isNaN(parsed) ? defaultValue : parsed;
    }
    return defaultValue;
  },

  // Sanitize string safely
  sanitizeString: (value) => {
    if (typeof value !== 'string') {
      return 'Unknown';
    }
    return value.trim() || 'Unknown';
  },

  // Get default document data
  getDefaultDocumentData: (key) => {
    return {
      url: '',
      name: documentHandler.generateDocumentName(key),
      words: 0,
      cost: 0,
      language: 'Unknown',
      type: 'unknown',
      isValid: false
    };
  },

  // Process documents collection with comprehensive error handling
  processDocuments: (documents, serviceType = null) => {
    // Handle null/undefined documents
    if (!documents) {
      return [];
    }

    // Handle non-object types
    if (typeof documents !== 'object') {
      console.warn('Documents is not an object:', typeof documents, documents);
      return [];
    }

    // Handle arrays
    if (Array.isArray(documents)) {
      return documents.map((doc, index) => 
        documentHandler.extractDocumentData(doc, `document_${index}`)
      );
    }

    // Handle objects
    try {
      return Object.entries(documents).map(([key, doc]) => 
        documentHandler.extractDocumentData(doc, key)
      );
    } catch (error) {
      console.error('Error processing documents:', error);
      return [];
    }
  },

  // Validate document data
  validateDocument: (docData) => {
    return {
      hasUrl: Boolean(docData.url && docData.isValid),
      hasName: Boolean(docData.name && docData.name !== 'Unknown Document'),
      hasWords: docData.words > 0,
      hasCost: docData.cost > 0,
      hasLanguage: docData.language !== 'Unknown',
      isValid: docData.isValid
    };
  },

  // Format document for display
  formatDocumentForDisplay: (docData) => {
    const validation = documentHandler.validateDocument(docData);
    
    return {
      ...docData,
      displayName: docData.name,
      displayUrl: docData.url,
      displayWords: docData.words.toLocaleString(),
      displayCost: `AED ${docData.cost.toLocaleString()}`,
      displayLanguage: docData.language,
      validation,
      // Add status indicators
      status: {
        hasValidUrl: validation.hasUrl,
        hasContent: validation.hasWords || validation.hasCost,
        isComplete: validation.hasUrl && validation.hasName
      }
    };
  },

  // Get document statistics
  getDocumentStats: (documents) => {
    const processedDocs = documentHandler.processDocuments(documents);
    
    return {
      total: processedDocs.length,
      withUrls: processedDocs.filter(doc => doc.isValid).length,
      totalWords: processedDocs.reduce((sum, doc) => sum + doc.words, 0),
      totalCost: processedDocs.reduce((sum, doc) => sum + doc.cost, 0),
      languages: [...new Set(processedDocs.map(doc => doc.language).filter(lang => lang !== 'Unknown'))],
      types: [...new Set(processedDocs.map(doc => doc.type).filter(type => type !== 'unknown'))]
    };
  },

  // Handle document errors gracefully
  handleDocumentError: (error, context = '') => {
    console.error(`Document handling error${context ? ` in ${context}` : ''}:`, error);
    return {
      url: '',
      name: 'Error loading document',
      words: 0,
      cost: 0,
      language: 'Unknown',
      type: 'error',
      isValid: false,
      error: error.message || 'Unknown error'
    };
  }
};

export default documentHandler;
